import { useEffect } from "react";
import Lottie from "lottie-react";
import Modal from "../../../components/Modal";
import confettiAnimation from "../../../assets/lotties/confetti.json";
import { TierProgressView } from "./components/TierProgressView";
import { useQuery } from "@tanstack/react-query";
import { api as badgeCenterApi } from "../../../services/apis/badge-center";
import { useUser } from "../../../services/user-context";
import { XpEarnedView } from "./components/XpEarnedView";
import { BadgeEarnedView } from "./components/BadgeEarnedView";
import { isBadgeCenterEnabled } from "../../../integrations/posthog/featureFlags";
import { usePostHogFeatureFlag } from "../../../integrations/posthog/usePostHogFeatureFlag";
import { ReactComponent as BadgeNotificationStarsSvg } from "../../../assets/images/badge-notification-stars.svg";
import { useNotificationQueue } from "./useNotificationQueue";
import {
    assignBadgeNotificationIds,
    BADGE_NOTIFICATION_TYPE,
    BadgeNotification,
    getBadgeNotificationKey,
    isSupportedBadgeNotification,
} from "./badgeNotificationKey";
const POLL_INTERVAL_MS = 1000;
const POLL_ERROR_DELAY_MS = 30000;

export function AwardPopup() {
    const { userInfo } = useUser() as { userInfo?: { id?: string } };
    const userId = userInfo?.id;
    const isBadgeCenterFlagOn = usePostHogFeatureFlag(isBadgeCenterEnabled);

    const enabled = !!userId && isBadgeCenterFlagOn;

    const { data } = useQuery<unknown>({
        queryKey: ["badge-center-notifications", userId],
        queryFn: () => badgeCenterApi.getBadgeCenterNotifications(),
        enabled,
        // Poll on a fixed cadence. React Query owns a single internal interval
        // timer that it re-creates on every settle and clears on unmount, so
        // overlapping timers or a stale retry firing after a success are not
        // possible. While the endpoint is failing, back off to a slow cadence
        // so a persistent error can never become a tight request loop.
        refetchInterval: (_data, query) =>
            query.state.status === "error"
                ? POLL_ERROR_DELAY_MS
                : POLL_INTERVAL_MS,
    });

    const { current, enqueue, dismiss } = useNotificationQueue<BadgeNotification>(
        getBadgeNotificationKey,
        userId,
    );

    useEffect(() => {
        if (data == null) return;
        // Only enqueue notifications we can actually render. Items with a
        // missing, unknown, or future type would otherwise open the popup and
        // block interaction with an empty card for the full auto-dismiss window.
        enqueue(
            assignBadgeNotificationIds(data).filter(isSupportedBadgeNotification),
        );
    }, [data, enqueue]);

    const title = current?.title ?? "";
    const description = current?.description ?? "";
    const metadata = current?.metadata;
    const type = metadata?.type;
    const showStars =
        type === BADGE_NOTIFICATION_TYPE.XP_EARNED ||
        type === BADGE_NOTIFICATION_TYPE.ACHIEVEMENT_UNLOCKED;

    return (
        <Modal
            isOpen={!!current}
            handleModal={dismiss}
            contentClassName="w-full h-full rounded-[12px] flex items-center justify-center !bg-[#18181B]/[0.70]"
        >
            {/* Click-outside catcher: the modal content fills the screen, so the
                Modal's own backdrop never receives clicks. This sibling sits
                behind the popup and dismisses on click. */}
            <div className="absolute inset-0" onClick={dismiss} aria-hidden="true" />
            <div
                className="relative overflow-hidden bg-background2 md:w-[475px] md:h-[350px] shadow-[0_2px_4px_-2px_rgba(0,0,0,0.50),0_4px_6px_-1px_rgba(0,0,0,0.10)] rounded-[12px] flex flex-col gap-4 items-center justify-center"
                onClick={(e) => e.stopPropagation()}
            >
                <Lottie
                    animationData={confettiAnimation}
                    loop={false}
                    autoplay
                    className="pointer-events-none absolute inset-0 w-full h-full"
                />
                {metadata?.type === BADGE_NOTIFICATION_TYPE.SUB_TIER_UP && (
                    <TierProgressView description={description} title={title} />
                )}
                {metadata?.type === BADGE_NOTIFICATION_TYPE.XP_EARNED && (
                    <XpEarnedView
                        title={title}
                        description={description}
                        metadata={metadata}
                    />
                )}
                {metadata?.type === BADGE_NOTIFICATION_TYPE.ACHIEVEMENT_UNLOCKED && (
                    <BadgeEarnedView
                        title={title}
                        description={description}
                        metadata={metadata}
                    />
                )}
                {showStars && (
                    <div className="absolute top-7 left-0 w-full flex items-center justify-center">
                        <BadgeNotificationStarsSvg />
                    </div>
                )}
            </div>
        </Modal>
    );
}
